/** The two Keycloak realms the app signs users in against. */
export type Realm = "b2b" | "lantbrukare";

interface RealmAuthorities {
  b2b: string;
  lantbrukare: string;
}

/** Keycloak's `iss` and a hand-typed authority may differ by a trailing slash. */
export const withoutTrailingSlash = (url: string): string => url.replace(/\/+$/, "");

/**
 * The realm authorities as configured at runtime.
 *
 * Read at call time, not at module load: `window.appConfig` is injected by
 * `public/config/appConfig.js` before the app starts, but a test has to be able
 * to set it after importing this module.
 */
export const configuredAuthorities = (): RealmAuthorities => ({
  b2b: window.appConfig.REACT_APP_B2B_IAM_AUTHORITY,
  lantbrukare: window.appConfig.REACT_APP_LANTBRUKARE_IAM_AUTHORITY,
});

/**
 * Which realm issued a token.
 *
 * oidc-client-ts rejects a token whose `iss` differs from `metadata.issuer`, and
 * both auth configs set that to the realm authority, so an exact comparison is
 * the same rule the library already enforces. A substring test on the realm
 * name (`/dev-b2b`) only held in dev and left staging and prod unenforced.
 *
 * @param issuer - the `iss` claim from the OIDC profile, if any
 * @param authorities - the realm authorities; defaults to the runtime config
 * @returns the realm, or null when the issuer matches neither. That cannot
 *   happen for a session the library accepted, so callers treat it as
 *   "unknown, do not redirect" rather than as a reason to sign out.
 */
export const realmFromIssuer = (
  issuer: string | undefined,
  authorities: RealmAuthorities = configuredAuthorities(),
): Realm | null => {
  if (!issuer) return null;

  const normalized = withoutTrailingSlash(issuer);
  if (normalized === withoutTrailingSlash(authorities.b2b)) return "b2b";
  if (normalized === withoutTrailingSlash(authorities.lantbrukare)) return "lantbrukare";
  return null;
};
